import { ICONS } from 'consts/consts';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Badge,
  Button,
  Collapse,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable, {
  TALBE_SIZE_NO_LIMIT,
} from 'views/Widgets/People/FilterablePeopleTable';
import { FORMAT_TEXT_ONLY } from 'views/Widgets/People/Filters/common';
import CreateCustomSkillModal from '../SkillsAdmin/CreateCustomSkillModal';
import { MetadataItem } from './useAgentMetadata';

interface CustomSkill {
  id: string;
  name: string;
  description?: string;
  instructions?: string;
  required_resources?: string[];
  created_at?: string;
  updated_at?: string;
}

interface CustomSkillSelectorProps {
  value?: CustomSkill[];
  onChange?: (event: CustomSkill[]) => void;
  name?: string;
  resourcesMetadata?: MetadataItem[];
}

const CustomSkillSelector: React.FC<CustomSkillSelectorProps> = ({
  value,
  onChange,
  name,
  resourcesMetadata = [],
}) => {
  const { formatMessage } = useIntl();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateSkillModalOpen, setIsCreateSkillModalOpen] = useState(false);
  const [selectedSkillsSet, setSelectedSkillsSet] = useState<Set<string>>(
    new Set()
  );
  const [expandedSkillId, setExpandedSkillId] = useState<string | null>(null);

  // Initialize selected skills set when value changes
  useEffect(() => {
    if (Array.isArray(value) && value.length > 0) {
      setSelectedSkillsSet(new Set(value.map((s) => s.id)));
    } else {
      setSelectedSkillsSet(new Set());
    }
  }, [value]);

  const { data, error, status } = useConfirmApi<{
    results: CustomSkill[];
    next?: string;
    count: number;
  }>({
    method: 'GET',
    url: 'ai-custom-skills',
    disabled: !isModalOpen,
  });

  const toggleModal = useCallback(() => {
    if (!isModalOpen) {
      // Reset selection to current value when opening modal
      if (Array.isArray(value) && value.length > 0) {
        setSelectedSkillsSet(new Set(value.map((s) => s.id)));
      } else {
        setSelectedSkillsSet(new Set());
      }
    }
    setIsModalOpen((prev) => !prev);
  }, [isModalOpen, value]);

  const toggleSelectedRowField = useCallback((rowId: string) => {
    setSelectedSkillsSet((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  const handleSave = useCallback(() => {
    if (!onChange || !name) {
      return;
    }

    const allSkills = data?.results || [];
    const selectedSkills = allSkills.filter((s) => selectedSkillsSet.has(s.id));

    onChange(selectedSkills);

    setIsModalOpen(false);
  }, [onChange, name, data, selectedSkillsSet]);

  // Handle various value types - form might pass object, array, null, or undefined
  const currentValue = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value]
  );

  // Build a map of resource IDs to names for display
  const resourceNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of resourcesMetadata) {
      map[r.id] = r.name;
    }
    return map;
  }, [resourcesMetadata]);

  const toggleExpandedSkill = useCallback((skillId: string) => {
    setExpandedSkillId((prev) => (prev === skillId ? null : skillId));
  }, []);

  // Render a single skill row with expandable instructions
  const renderSkillRow = useCallback(
    (row: CustomSkill) => {
      const isExpanded = expandedSkillId === row.id;
      const resources = row.required_resources ?? [];
      return (
        <div key={row.id} className="list-group-item p-0">
          <div
            className="d-flex align-items-center px-3 py-2"
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={() => toggleExpandedSkill(row.id)}
          >
            <div className="flex-grow-1">
              <div className="d-flex gap-3">
                <strong style={{ minWidth: '220px', flexShrink: 0 }}>
                  {row.name}
                </strong>
                <div>
                  <span className="text-muted small">{row.description}</span>
                  {resources.length > 0 && (
                    <div className="mt-2 d-flex flex-wrap gap-1">
                      {resources.map((rId) => (
                        <Badge key={rId} color="light" className="px-2 py-1">
                          <i className="fe fe-database me-1" />
                          {resourceNameMap[rId] || rId}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <i
              className={`fe fe-chevron-${
                isExpanded ? 'up' : 'down'
              } ms-3 text-muted`}
            />
          </div>
          <Collapse isOpen={isExpanded}>
            {row.instructions && (
              <div className="px-3 pb-3">
                <pre
                  className="small mb-0 p-2 bg-light rounded"
                  style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                >
                  {row.instructions}
                </pre>
              </div>
            )}
          </Collapse>
        </div>
      );
    },
    [expandedSkillId, toggleExpandedSkill, resourceNameMap]
  );

  const getUniqueRowId = useCallback((row: CustomSkill) => row.id, []);

  const handleOpenCreateSkillModal = useCallback(
    () => setIsCreateSkillModalOpen(true),
    []
  );
  const handleCloseCreateSkillModal = useCallback(
    () => setIsCreateSkillModalOpen(false),
    []
  );
  const handleCreateSkillSuccess = useCallback(
    (createdSkill: any) => {
      setIsCreateSkillModalOpen(false);
      if (onChange && name) {
        onChange([...currentValue, createdSkill]);
      }
    },
    [onChange, name, currentValue]
  );
  const stopPropagation = useCallback(
    (e: React.SyntheticEvent) => e.stopPropagation(),
    []
  );

  // State for expanded skill in the modal
  const [expandedModalSkillId, setExpandedModalSkillId] = useState<
    string | null
  >(null);
  const toggleExpandedModalSkill = useCallback((skillId: string) => {
    setExpandedModalSkillId((prev) => (prev === skillId ? null : skillId));
  }, []);

  // Columns for the modal with checkbox support
  const modalColumns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.ai.agents.agents_admin.custom_skill_selector.table.name',
          defaultMessage: 'Skill Name',
        }),
        field: 'person',
        className: 'col-auto',
        sortable: false,
        format: FORMAT_TEXT_ONLY,
      },
      {
        name: formatMessage({
          id: 'app.views.ai.agents.agents_admin.custom_skill_selector.table.description',
          defaultMessage: 'Description',
        }),
        field: 'detail',
        className: 'col',
        sortable: false,
        format: (
          _val: unknown,
          _uniqueId: string,
          _rowIndex: number,
          row: {
            id: string;
            description?: string;
            required_resources?: string[];
            instructions?: string;
          }
        ) => {
          const isExpanded = expandedModalSkillId === row.id;
          const resources = row.required_resources ?? [];
          return (
            <div>
              <span className="text-muted small">{row.description}</span>
              {resources.length > 0 && (
                <div className="mt-2 d-flex flex-wrap gap-1">
                  {resources.map((rId) => (
                    <Badge key={rId} color="light" className="px-2 py-1">
                      <i className="fe fe-database me-1" />
                      {resourceNameMap[rId] || rId}
                    </Badge>
                  ))}
                </div>
              )}
              {row.instructions && (
                <>
                  <div className="mt-2">
                    <Button
                      color="link"
                      size="sm"
                      className="text-muted p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpandedModalSkill(row.id);
                      }}
                    >
                      <i
                        className={`fe fe-chevron-${
                          isExpanded ? 'up' : 'down'
                        } me-1`}
                      />
                      <FormattedMessage
                        id="app.views.ai.agents.agents_admin.custom_skill_selector.toggle_instructions"
                        defaultMessage="{action} instructions"
                        values={{
                          action: isExpanded ? 'Hide' : 'Show',
                        }}
                      />
                    </Button>
                  </div>
                  <Collapse isOpen={isExpanded}>
                    <pre
                      className="small mb-0 mt-1 p-2 bg-light rounded"
                      style={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit',
                      }}
                    >
                      {row.instructions}
                    </pre>
                  </Collapse>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [
      formatMessage,
      resourceNameMap,
      expandedModalSkillId,
      toggleExpandedModalSkill,
    ]
  );

  // Transform skills to include a fake person object for checkbox rendering
  const modalRows = useMemo(() => {
    if (!data?.results) return [];

    return data.results.map((skill) => ({
      ...skill,
      detail: null,
      person: {
        id: skill.id,
        full_name: skill.name,
        avatar: null,
      },
    }));
  }, [data]);

  return (
    <>
      <div className="mb-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <Button color="primary" onClick={toggleModal} size="sm">
            <i className={ICONS.RESUME + ' me-2'} />
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.custom_skill_selector.edit_skills"
              defaultMessage="Edit Custom Skills"
            />
          </Button>
          <Button
            color="white"
            className="border"
            onClick={handleOpenCreateSkillModal}
            size="sm"
          >
            <i className="fe fe-plus me-1" />
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.custom_skill_selector.create_new"
              defaultMessage="New Custom Skill"
            />
          </Button>
          {currentValue.length > 0 && (
            <span className="text-muted small">
              <FormattedMessage
                id="app.views.ai.agents.agents_admin.custom_skill_selector.selected_count"
                defaultMessage="{count} custom {count, plural, one {skill} other {skills}} selected"
                values={{ count: currentValue.length }}
              />
            </span>
          )}
        </div>
      </div>

      <div>
        {currentValue.length === 0 ? (
          <p className="text-muted small">
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.custom_skill_selector.no_skills"
              defaultMessage="No custom skills selected. Click 'Edit Custom Skills' to select skills."
            />
          </p>
        ) : (
          <div className="list-group">{currentValue.map(renderSkillRow)}</div>
        )}
      </div>

      <Modal isOpen={isModalOpen} toggle={toggleModal} size="xl">
        <ModalHeader toggle={toggleModal}>
          <FormattedMessage
            id="app.views.ai.agents.agents_admin.custom_skill_selector.modal.title"
            defaultMessage="Select Custom Skills"
          />
        </ModalHeader>
        <ModalBody>
          {status === 'LOADING' && (
            <Loading
              layout="overlay"
              message={formatMessage({
                id: 'app.views.ai.agents.agents_admin.custom_skill_selector.modal.loading',
                defaultMessage: 'Loading custom skills...',
              })}
            />
          )}
          {!!error && <SimpleErrorPage error={error} />}
          {status === 'SUCCESS' && data && (
            <>
              <p className="text-muted mb-3">
                <FormattedMessage
                  id="app.views.ai.agents.agents_admin.custom_skill_selector.modal.description"
                  defaultMessage="Select custom skills by clicking the checkboxes on the left. Custom skills provide specialized instructions for your agent."
                />
              </p>
              <FilterablePeopleTable
                rows={modalRows}
                columns={modalColumns}
                hideFilters={false}
                hideExportButton={true}
                initialTableSize={TALBE_SIZE_NO_LIMIT}
                toggleSelectedRowField={toggleSelectedRowField}
                getUniqueRowId={getUniqueRowId}
                selectedRowToggleFieldsSet={selectedSkillsSet}
                setSelectedRowToggleFieldsSet={setSelectedSkillsSet}
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.custom_skill_selector.modal.cancel"
              defaultMessage="Cancel"
            />
          </Button>
          <Button color="primary" onClick={handleSave}>
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.custom_skill_selector.modal.save"
              defaultMessage="Save"
            />
          </Button>
        </ModalFooter>
      </Modal>
      {isCreateSkillModalOpen && (
        <div onSubmit={stopPropagation} onClick={stopPropagation}>
          <CreateCustomSkillModal
            isOpen={isCreateSkillModalOpen}
            toggle={handleCloseCreateSkillModal}
            onSuccess={handleCreateSkillSuccess}
            resources={resourcesMetadata}
          />
        </div>
      )}
    </>
  );
};

export default CustomSkillSelector;
